<template>
    <div class="page">
        <Banner
            v-if="$config.exclusiveEventsLandingPageMerchandisingBanner"
            :background-color="$config.exclusiveEventsLandingPageMerchandisingBanner.bannerColor"
            :tagline="$config.exclusiveEventsLandingPageMerchandisingBanner.tagline"
            :title="$config.exclusiveEventsLandingPageMerchandisingBanner.title"
        />
        <section class="filters">
            <div class="filters__segment-filter">
                <div class="segment-filter__shop-by">Shop by&nbsp;</div>
                <Tabs
                    :default-item="segmentItems[0]"
                    :items="segmentItems"
                    @reset-tabs-dropdown="$router.push(makeSegmentRoute('all'))"
                />
            </div>
            <div
                class="filters__location-date"
            >
                <div
                    class="filter filter--locations">
                    <span class="icon-location-marker"></span>
                    <fx-select
                        v-model="locationId"
                        :default-option="{ label: 'Any Location', value: null }"
                        :disable-native="true"
                        :options="locations"
                        @input="onLocationIdSelected"
                    >
                        <span class="icon-chevron-lg-down" slot="caret"></span>
                    </fx-select>
                </div>
                <div class="filter filter--date">
                    <span class="icon-calendar-blank"></span>
                    <fx-date-range
                        align="left"
                        data-e2e="search.date-filter"
                        min-date="today"
                        v-model="dateRange"
                        :custom-option-copy="'Choose Date'"
                        :default-option-copy="'Any Date'"
                        :presets="dateRangeFilterPresets"
                        @input="onDateRangeSelected"
                    >
                        <span class="icon-chevron-lg-down" slot="caret"></span>
                    </fx-date-range>
                </div>
            </div>
        </section>

        <section>
            <EventList
                :events="events"
                :filters-applied="Boolean(segment !== 'all' || locationId || dateRange)"
                :format-points="formatPoints"
                :is-loading-events="isLoadingEvents"
                :is-loading-more-events="isLoadingMoreEvents"
                :is-loyalty-pricing="$store.state.isRedeemingLoyaltyPoints"
                :load-more-copy="'Show More'"
                :loading-more-copy="'Loading More Events...'"
                :make-event-route="makeEventRoute"
                :pagination="pagination">
            </EventList>
        </section>
    </div>
</template>

<script>
import { CAPITAL_ONE_EXCLUSIVE_SEGMENTS } from '../constants'
import { mapAggregateCollectionToDropdownOptions, sortDropdownOptions } from '../services/EventAggregatesService.js'
import { mixin as onClickOutside } from 'vue-on-click-outside'
import Banner from '../components/BannerComponent.vue'
import DateUtil from 'core/utilities/DateUtil'
import errorHandleable from '../mixins/ErrorHandleable'
import eventFilterable from '../mixins/EventFilterable'
import eventListable from '../mixins/EventListable'
import loyaltyFormattable from '../mixins/LoyaltyFormattable'
import quebecUserModal from '../mixins/QuebecUserModal'
import resizable from '../mixins/Resizable'
import selectComponent from 'core/components/SelectComponent.vue'
import Tabs from '../components/Tabs.vue'

export default {
    beforeCreate () {
        this.htmlPageTitle = `Exclusive tickets at ${this.$config.partnerName}`
        this.CAPITAL_ONE_EXCLUSIVE_SEGMENTS = CAPITAL_ONE_EXCLUSIVE_SEGMENTS
    },
    components: {
        'fx-select': selectComponent,
        Banner,
        Tabs
    },
    methods: {
        closeSegmentDropDown () {
            this.isSegmentDropdownOpen = false
        },
        fetchEvents () {
            return this.$store.state.userLocationPromise
                .then(userLocation => {
                    return this.$marketplace
                        .getExclusiveEvents({
                            partner_tag: 'cash',
                            all_in_price: this.$config.isAllInPricing ? 1 : 0,
                            category: this.exclusiveCategory,
                            date_end: this.serializedEndDate,
                            date_start: this.serializedStartDate,
                            location_id: this.locationId,
                            utc_offset: DateUtil.format(new Date(), 'Z'),
                            page: this.page,
                            per_page: 10
                        })
                })
                .then(response => {
                    this.locations = sortDropdownOptions(mapAggregateCollectionToDropdownOptions(response.aggregations.results.locations || []))
                    return {
                        events: response.chronological.results,
                        pagination: response.chronological.meta
                    }
                })
        },
        makeSegmentRoute (key) {
            const query = { ...this.$route.query }

            if (key) {
                query.segment = key
            }

            return { query }
        },
        onClickSegmentDropdown () {
            this.isSegmentDropdownOpen = !this.isSegmentDropdownOpen
        },
        resetSegmentDropDown () {
            this.$router.push(this.makeSegmentRoute('all'))
            this.closeSegmentDropDown()
        }
    },
    computed: {
        exclusiveCategory () {
            if (this.$route.query.segment) {
                return this.CAPITAL_ONE_EXCLUSIVE_SEGMENTS[this.$route.query.segment]
            } else {
                return this.CAPITAL_ONE_EXCLUSIVE_SEGMENTS.all
            }
        },
        selectedSegment () {
            return {
                name: 'Segment: ' + (this.segment || 'all events'),
                label: this.segment ? this.segmentItems.find(segment => segment.segment === this.segment).label : 'All Events'
            }
        },
        segmentItems () {
            return [
                {
                    segment: 'all',
                    isActive: this.isAllEvents,
                    label: 'All Events',
                    to: this.makeSegmentRoute('all')
                },
                {
                    segment: 'dining',
                    isActive: this.isDiningEvents,
                    label: 'Dining',
                    to: this.makeSegmentRoute('dining')
                },
                {
                    segment: 'entertainment',
                    isActive: this.isEntertainmentEvents,
                    label: 'Entertainment',
                    to: this.makeSegmentRoute('entertainment')
                },
                {
                    segment: 'sports',
                    isActive: this.isSportsEvents,
                    label: 'Sports',
                    to: this.makeSegmentRoute('sports')
                }
            ]
        }
    },
    data () {
        return {
            isAllEvents: true,
            isDiningEvents: false,
            isEntertainmentEvents: false,
            isSegmentDropdownOpen: false,
            isSportsEvents: false,
            locations: []
        }
    },
    mixins: [
        errorHandleable,
        eventFilterable,
        eventListable,
        loyaltyFormattable,
        onClickOutside,
        quebecUserModal,
        resizable
    ],
    watch: {
        $route: {
            immediate: true,
            handler () {
                this.isAllEvents = Boolean(this.$route.query.segment === 'all' || !this.$route.query.segment)
                this.isDiningEvents = Boolean(this.$route.query.segment === 'dining')
                this.isEntertainmentEvents = Boolean(this.$route.query.segment === 'entertainment')
                this.isSportsEvents = Boolean(this.$route.query.segment === 'sports')

                if (this.isAllEvents) { this.segment = 'all' }
            }
        }
    }
}
</script>

<style lang="postcss" scoped>
@import 'theme';

.filters {
    -webkit-font-smoothing: antialiased;
    display: flex;
    flex-direction: column;
    margin-bottom: 8px;

    @media (--mq-large-min) {
        align-items: center;
        flex-direction: row;
    }

    & .filters__location-date {
        display: flex;
        flex-direction: row;
        margin-left: 0;
        margin-top: 24px;
        width: 100%;
        @media (--mq-large-min) {
            margin-left: 24px;
            margin-top: 55px;
            width: auto;
        }
    }

    & .filters__segment-filter {
        display: flex;
        flex-direction: row;

        @media (--mq-large-min) {
            flex-direction: column;
        }

        & .segment-filter__shop-by {
            font-size: 20px;
            @media (--mq-large-min) {
                font-size: 24px;
                margin-bottom: 24px;
            }
        }
    }

}

.filter {
    align-self: flex-end;
    color: var(--c-gray-5);
    font-size: 16px;
    font-weight: 300;
    padding: 0;

    &.filter--date {
        padding-left: 16px;
        @media (--mq-large-min) {
            margin-left: auto;
            padding-left: 24px;
        }

        & .icon-calendar-blank {
            color: #808689;
            font-size: var(--fs-large);
            margin-right: 4px;
            position: relative;
            top: 2px;
        }

        & >>> .icon-cancel {
            color: #808689;
            font-size: var(--fs-large);
            font-weight: var(--fw-light);
            position: relative;
            text-decoration: none;
            right: 3px;
            top: 4px;
        }

        & >>> .icon-chevron-lg-down {
            color: #808689;
            font-size: 14px;
            position: relative;
            right: 3px;
            top: 3px;
        }
    }

    &.filter--opponent {
        color: var(--c-accent-text);
        white-space: nowrap;
        font-size: var(--fs-large);
    }

    &.filter--locations {
        display: flex;
        & >>> select {
            border: none;
            color: var(--c-accent-text);
            font-size: 16px;
            padding-bottom: 0px;
            text-decoration: underline;
        }
        & .icon-location-marker {
            top: 5px;
        }

        & >>> .icon-cancel {
            color: var(--c-gray-5);
            font-size: 16px;
            position: absolute;
            right: 0;
            top: 6px;
        }
    }

    & .fx-date-range {
        display: inline-block;
    }

    & .icon-calendar-blank,
    & .icon-location-marker {
        font-size: 18px;
        position: relative;
        top: 2px;
    }

    & >>> .fx-select-custom {
        padding: 0 14px 0 0;
    }

    & >>> .fx-selection {
        font-size: var(--fs-large);
        font-weight: var(--fw-light);
        text-decoration: underline;
    }

    & >>> .icon-cancel {
        color: var(--c-accent-text);
        font-size: 14px;
        position: absolute;
        right: 0;
        top: 8px;
    }

    & >>> .fx-select-input {
        border-bottom: 0;
        color: var(--c-gray-5);
    }

    & >>> .icon-chevron-lg-down {
        color: var(--c-gray-5);
        font-size: 14px;
        position: absolute;
        right: 0;
        top: 7px;
    }
}

.page {
    padding: 0 var(--site-padding) 48px;
    width: 100%;

    @media (--mq-full-min) {
        margin: 0 auto 0;
        width: var(--site-container-width);
    }
}

.segment-dropdown {
    color: #444649;
    font-size: 20px;

    & .sement-dropdown-cancel {
            font-size: 16px;
            position: relative;
            top: 2px;
    }

    & .segment-dropdown__option-wrapper {
        align-items: center;
        border-bottom: solid 1px #E7E9E9;
        cursor: pointer;
        display: flex;
        font-size: 16px;
        outline: none;
        white-space: nowrap;

        &:focus, &:hover, &.segment-dropdown__option-wrapper--active {
            background: var(--c-dropdown-active-item-bg);
            border-left: 4px solid var(--c-interact-active);
            color: var(--c-accent-text);
        }
    }

    & .segment-dropdown__option {
        height: 100%;
        padding: 10px 16px;
        width: 100%;
    }

    & .segment-dropdown__current-option {
        text-decoration: underline;
    }

    & >>> .dropdown-container {
        -webkit-box-shadow: 0 0 2px rgba(0, 0, 0, .5);
        background-color: white;
        border-radius: 4px;
        box-shadow: 0 0 2px rgba(0, 0, 0, .5);
        overflow-y: auto;
        z-index: 40;
    }

    & >>> .icon-chevron-lg-down {
        font-size: 14px;
        padding-left: 4px;
    }
}
</style>
